import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FC, useEffect, useState } from 'react';

import { useGetInstancesQuery, useTakeControlMutation } from '@api/connectInstance';
import { useGetMyProfileQuery } from '@api/user';
import { RdConnectInstance } from '@apiContract';
import { Label } from '@components/Label';
import { ClickableBox } from '@uiKit/ClickableBox';
import capitalize from 'lodash/fp/capitalize';
import { toast } from 'sonner';

import { getRdConnectInstancePing } from '@src/api/automatedCapture';

import { setSelectInstallationDialog } from '@redux/slices/app';
import { useDispatch, useSelector } from '@redux/store';

import { sxInstallationCard } from './styles';

const getInstallationName = (inst: RdConnectInstance) => inst.name || `Installation #${inst.id}`;

const getConnectedUserLabel = (inst: RdConnectInstance, currentUserId: string | null) => {
  if (inst.connectedUserId === currentUserId) return 'Connected user: You';
  if (inst.connectedUser?.displayName) return `Connected user: ${inst.connectedUser.displayName}`;
  if (inst.connectedUserId) return 'Connected user: Another user';
  return 'Connected user: None';
};

enum PingStatus {
  Loading = 'loading',
  Online = 'online',
  Offline = 'offline',
}

type InstallationItemProps = {
  inst: RdConnectInstance;
  isSelected: boolean;
  currentUserId: string | null;
  onSelect: (id: number) => void;
};

const InstallationItem: FC<InstallationItemProps> = ({ inst, isSelected, currentUserId, onSelect }) => {
  const theme = useTheme();
  const isConnectedToMe = inst.connectedUserId === currentUserId;
  const [pingStatus, setPingStatus] = useState<PingStatus>(PingStatus.Loading);

  useEffect(() => {
    if (!inst.id) {
      setPingStatus(PingStatus.Offline);
      return;
    }

    let cancelled = false;
    setPingStatus(PingStatus.Loading);

    getRdConnectInstancePing({ rdConnectInstanceId: inst.id })
      .then(() => {
        if (!cancelled) setPingStatus(PingStatus.Online);
      })
      .catch(() => {
        if (!cancelled) setPingStatus(PingStatus.Offline);
      });

    return () => {
      cancelled = true;
    };
  }, [inst.id]);

  return (
    <ClickableBox
      onClick={() => !isConnectedToMe && inst.id && onSelect(inst.id)}
      sx={sxInstallationCard(isSelected, isConnectedToMe, theme)}
    >
      <Box>
        <Typography variant="subtitle2">{getInstallationName(inst)}</Typography>
        <Typography variant="body2" color="text.secondary">
          {getConnectedUserLabel(inst, currentUserId)}
        </Typography>
      </Box>
      {pingStatus === PingStatus.Loading ? (
        <CircularProgress size={16} />
      ) : (
        <Label color={pingStatus === PingStatus.Online ? 'success' : 'error'} variant="soft">
          {capitalize(pingStatus)}
        </Label>
      )}
    </ClickableBox>
  );
};

const SelectInstallationDialog: FC = () => {
  const dispatch = useDispatch();
  const { open } = useSelector((state) => state.app.selectInstallationDialog);

  const { data: instances = [] } = useGetInstancesQuery(undefined, { skip: !open });
  const { data: currentUser } = useGetMyProfileQuery();
  const currentUserId = currentUser?.id ?? null;

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [takeControl, { isLoading }] = useTakeControlMutation();

  const handleClose = () => {
    dispatch(setSelectInstallationDialog({ open: false, instanceId: undefined, controllingUserId: undefined }));
    setSelectedId(null);
  };

  const handleConnect = async () => {
    if (!selectedId) return;
    try {
      await takeControl({ rdConnectInstanceId: selectedId }).unwrap();
      toast.success('Connected to installation');
      handleClose();
    } catch {
      toast.error('Failed to connect');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select a Received Connect installation</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Selecting an installation with a connected user will disconnect that user until they decide to take control
          again.
        </Typography>

        {instances.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No Received Connect installations available. Add installations from the Company &rarr; Subscription tab.
          </Typography>
        ) : (
          instances.map((inst) => (
            <InstallationItem
              key={inst.id}
              inst={inst}
              isSelected={selectedId === inst.id}
              currentUserId={currentUserId}
              onSelect={setSelectedId}
            />
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={handleConnect}
          disabled={!selectedId}
          loading={isLoading}
        >
          Connect
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default SelectInstallationDialog;
